import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Dropdown,
  Progress,
  Tooltip,
} from "flowbite-react";
import {
  HiArrowLeft,
  HiChatBubbleLeftRight,
  HiCheck,
  HiClock,
  HiEllipsisVertical,
  HiPaperClip,
  HiPencilSquare,
  HiPlus,
  HiTag,
  HiUser,
} from "react-icons/hi2";
import { CRM } from "../config/apiConfig";
import axiosInstance from "../config/axiosInstance";
import moment from "moment";
import AttachmentPicker from "../components/inputs/AttachmentPicker";
import { HiTrash } from "react-icons/hi";
import { BsCheck } from "react-icons/bs";

/** =============================================================
 *  Utils
 *  ============================================================= */
const cx = (...c) => c.filter(Boolean).join(" ");
const BR = (d) => (d ? moment(d).format("DD/MM/YYYY HH:mm") : "—");

const Section = ({ title, right, children, id, className }) => (
  <section id={id} className={cx("mb-6", className)}>
    <div className="mb-3 flex items-center justify-between">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        {title}
      </h2>
      {right}
    </div>
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0b0f1a]">
      {children}
    </div>
  </section>
);

const Skeleton = ({ rows = 3 }) => (
  <div className="animate-pulse space-y-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="h-4 w-full rounded bg-slate-200/60 dark:bg-white/10"
      />
    ))}
  </div>
);

/** =============================================================
 *  Page
 *  ============================================================= */
export default function CardDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { search, hash } = useLocation();
  const q = useMemo(() => new URLSearchParams(search), [search]);
  const queryClient = useQueryClient();

  const [highlightId, setHighlightId] = useState(null);
  const [cardFiles, setCardFiles] = useState([]);
  const [cardUploadProgress, setCardUploadProgress] = useState(0);

  // ================= Card =================
  const {
    data: card,
    isLoading: cardLoading,
    isError: cardError,
  } = useQuery(
    ["card", id],
    async () => {
      const { data } = await axiosInstance.get(`${CRM.GET_CARD}/${id}/details`);
      return data?.data;
    },
    { refetchOnWindowFocus: false }
  );

  // ================= Tasks =================
  const { data: tasks = [], isLoading: tasksLoading } = useQuery(
    ["cardTasks", id],
    async () => {
      const { data } = await axiosInstance.get(`${CRM.LIST_TASKS}/${id}/tasks`);
      return data?.data ?? [];
    },
    { refetchOnWindowFocus: false }
  );

  // ================= History =================
  const { data: history = [], isLoading: historyLoading } = useQuery(
    ["cardHistory", id],
    async () => {
      const { data } = await axiosInstance.get(
        `${CRM.GET_CARD_HISTORY}/${id}/history`
      );
      return data?.data ?? [];
    },
    { refetchOnWindowFocus: false }
  );

  // Mutations
  const completeTaskMutation = useMutation(
    async (taskId) => {
      const { data } = await axiosInstance.post(
        `${CRM.COMPLETE_TASK}/${taskId}/complete`
      );
      return data?.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["cardTasks", id]),
    }
  );

  const deleteTaskMutation = useMutation(
    async (taskId) => {
      const { data } = await axiosInstance.delete(
        `${CRM.DELETE_TASK}/${taskId}`
      );
      return data?.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["cardTasks", id]),
    }
  );

  const uploadCardAttachmentsMutation = useMutation(
    async (files = []) => {
      if (!Array.isArray(files) || !files.length) return [];

      const fd = new FormData();
      files.forEach((file) => fd.append("attachments", file));

      const { data } = await axiosInstance.post(
        CRM.CARD_ATTACHMENTS(id),
        fd,
        {
          headers: { "Content-Type": undefined },
          onUploadProgress: (event) => {
            if (event?.total) {
              const pct = Math.round((event.loaded * 100) / event.total);
              setCardUploadProgress(pct);
            }
          },
        }
      );

      return data?.data ?? [];
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["card", id]);
        setCardFiles([]);
        setCardUploadProgress(0);
      },
      onSettled: () => {
        setCardUploadProgress(0);
      },
    }
  );

  const triggerCardUpload = () => {
    if (!cardFiles.length) return;
    uploadCardAttachmentsMutation.mutate(cardFiles);
  };

  // Highlight vindo da notificação (?highlight=history:123 ou ?highlight=task:456)
  useEffect(() => {
    const h = q.get("highlight");
    if (h) setHighlightId(h);
  }, [q]);

  // Scroll suave para âncoras (#tasks, #history, #attachments)
  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, cardLoading]);

  const stats = useMemo(() => {
    const total = tasks?.length || 0;
    const done =
      tasks?.filter((t) => String(t.status).toLowerCase() === "done").length ||
      0;
    const progress = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, progress };
  }, [tasks]);

  const cardAttachments = card?.attachments || [];
  const taskAttachments = useMemo(
    () =>
      tasks.flatMap((t) =>
        (t.attachments || []).map((att) => ({ ...att, taskId: t.id }))
      ),
    [tasks]
  );

  const responsible = card?.responsible || card?.Responsible || null;

  // ===================== UI =====================
  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      {/* Sticky Gradient Header */}
      <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-0 bg-gradient-to-b from-white/90 to-white/60 px-2 py-3 backdrop-blur dark:from-[#0b0f1a]/90 dark:to-[#0b0f1a]/60 md:-mx-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {cardLoading ? "Carregando..." : card?.title || "Card"}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                {(card?.stage?.name || card?.stageName) && (
                  <Badge color="info">
                    {card?.stage?.name || card?.stageName}
                  </Badge>
                )}
                {card?.pipeline?.name && (
                  <Badge color="gray">Funil: {card.pipeline.name}</Badge>
                )}
                {Number(card?.probability) > 0 && (
                  <Badge color="purple">
                    Prob.: {Number(card.probability)}%
                  </Badge>
                )}
                <Badge color="success">
                  Valor:{" "}
                  {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(Number(card?.value || 0))}
                </Badge>
                {card?.updatedAt && (
                  <span>Atualizado em {BR(card.updatedAt)}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* LEFT: Overview + History */}
        <div className="lg:col-span-2">
          {/* Overview */}
          <Section>
            {cardLoading ? (
              <Skeleton rows={6} />
            ) : cardError ? (
              <Alert color="failure">Erro ao carregar o card.</Alert>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Responsible */}
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 dark:border-white/10">
                  <Avatar
                    img={responsible?.avatar}
                    rounded
                    size="sm"
                    alt={responsible?.name}
                  />
                  <div className="text-sm">
                    <div className="text-slate-500">Responsável</div>
                    <div className="font-medium text-slate-900 dark:text-slate-100">
                      {responsible?.name || "—"}
                    </div>
                  </div>
                </div>

                {/* Customer */}
                <InfoCell
                  label="Cliente"
                  value={card?.customer?.name || card?.tenant?.name || "—"}
                  icon={<HiUser className="h-4 w-4" />}
                />
                <InfoCell
                  label="Telefone"
                  value={card?.customer?.phone || card?.phone || "—"}
                />
                <InfoCell
                  label="E-mail"
                  value={card?.customer?.email || card?.email || "—"}
                />

                {/* Dates */}
                <InfoCell
                  label="Criado"
                  value={card?.createdAt ? BR(card.createdAt) : "—"}
                  icon={<HiClock className="h-4 w-4" />}
                />
                <InfoCell
                  label="Atualizado"
                  value={card?.updatedAt ? BR(card.updatedAt) : "—"}
                  icon={<HiCheck className="h-4 w-4" />}
                />

                {/* Tags */}
                <div className="md:col-span-2">
                  <div className="text-xs text-slate-500">Tags</div>
                  {card?.tags?.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {card.tags.map((t) => (
                        <Badge key={t.id || t} color="gray">
                          <HiTag className="mr-1 inline h-3 w-3" />
                          {t.name || t}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-1 text-sm text-slate-500">Sem tags.</div>
                  )}
                </div>
              </div>
            )}
          </Section>

          {/* Tasks */}
          <Section
            id="tasks"
            title="Tarefas"
            right={
              <Button
                size="sm"
                onClick={() => navigate(`/crm/card/${id}/task/new`)}
              >
                <div className="flex items-center">
                  <HiPlus className="mr-2" />
                  <span> Nova tarefa</span>
                </div>
              </Button>
            }
            className="scroll-mt-20"
          >
            {tasksLoading ? (
              <Skeleton rows={5} />
            ) : tasks?.length === 0 ? (
              <EmptyState
                icon={<HiClock className="h-5 w-5" />}
                title="Sem tarefas"
                subtitle="Crie uma tarefa para organizar suas próximas ações."
              />
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-white/10">
                {tasks.map((t) => {
                  const key = `task:${t.id}`;
                  const highlighted = highlightId === key;
                  return (
                    <li
                      key={t.id}
                      id={key}
                      className={cx(
                        "py-3",
                        highlighted &&
                          "rounded-lg bg-amber-50/80 ring-2 ring-amber-300 dark:bg-amber-500/10"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <Badge
                              color={
                                t.status === "done"
                                  ? "success"
                                  : t.status === "cancelled"
                                  ? "gray"
                                  : "warning"
                              }
                            >
                              {String(t.status || "aberto").toUpperCase()}
                            </Badge>
                            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {t.type || "Tarefa"}
                            </span>
                            {t.dueDate && (
                              <span className="text-xs text-slate-500">
                                • vence {BR(t.dueDate)}
                              </span>
                            )}
                          </div>
                          {t.note && (
                            <div
                              className="prose prose-sm max-w-none dark:prose-invert"
                              dangerouslySetInnerHTML={{ __html: t.note }}
                            />
                          )}
                          {t.attachments?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {t.attachments.map((a) => (
                                <AttachmentPill
                                  key={a.id}
                                  att={a}
                                  cardId={id}
                                  taskId={t.id}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {t.status !== "done" && (
                            <Button
                              size="xs"
                              color="success"
                              onClick={() => completeTaskMutation.mutate(t.id)}
                            >
                              <BsCheck className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="xs"
                            color="failure"
                            onClick={() => deleteTaskMutation.mutate(t.id)}
                          >
                            <HiTrash className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
        </div>

        {/* RIGHT: Tasks + Metrics */}
        <div>
          {/* KPIs */}
          <div className="mb-6 mt-3 grid grid-cols-2 gap-3">
            <KpiCard label="Tarefas" value={stats.total} />
            <KpiCard label="Concluídas" value={stats.done} />
          </div>

          {/* History */}
          <Section id="history" title="Histórico" className="scroll-mt-20">
            {historyLoading ? (
              <Skeleton rows={6} />
            ) : history?.length === 0 ? (
              <EmptyState
                icon={<HiChatBubbleLeftRight className="h-5 w-5" />}
                title="Sem histórico"
                subtitle="As anotações e atividades aparecerão aqui."
              />
            ) : (
              <ul className="space-y-3">
                {history.map((h) => {
                  const key = `history:${h.id}`;
                  const highlighted = highlightId === key;
                  return (
                    <li
                      key={h.id}
                      id={key}
                      className={cx(
                        "rounded-xl border border-slate-200 p-3 dark:border-white/10",
                        highlighted &&
                          "bg-amber-50/80 ring-2 ring-amber-300 dark:bg-amber-500/10"
                      )}
                    >
                      <div className="mb-1 flex items-center gap-2">
                        <Avatar img={h.user?.avatar} rounded size="xs" />
                        <div className="text-sm text-slate-600 dark:text-slate-300">
                          <span className="font-medium">
                            {h.user?.name || "Usuário"}
                          </span>
                          <span className="ml-2 text-xs text-slate-500">
                            {BR(h.createdAt)}
                          </span>
                          {h.isEdited && (
                            <Badge className="ml-2" color="gray">
                              editado
                            </Badge>
                          )}
                        </div>
                      </div>
                      {h.type && (
                        <div className="mb-1 text-xs uppercase tracking-wide text-slate-400">
                          {h.type}
                        </div>
                      )}
                      {h.text && (
                        <div
                          className="prose prose-sm max-w-none dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: h.text }}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          {/* Attachments */}
          <Section id="attachments" title="Anexos" className="scroll-mt-20">
            {tasksLoading ? (
              <Skeleton rows={3} />
            ) : (
              <div className="space-y-4">
                <AttachmentPicker
                  files={cardFiles}
                  onChange={setCardFiles}
                  label="Adicionar anexos ao card"
                  hint="Arraste e solte ou clique para abrir o seletor"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={triggerCardUpload}
                    disabled={
                      !cardFiles.length || uploadCardAttachmentsMutation.isLoading
                    }
                  >
                    {uploadCardAttachmentsMutation.isLoading
                      ? "Enviando..."
                      : "Enviar anexos"}
                  </Button>
                  {uploadCardAttachmentsMutation.isLoading && (
                    <span className="text-xs text-slate-500">
                      {cardUploadProgress}% enviado
                    </span>
                  )}
                </div>
                {cardUploadProgress > 0 && cardUploadProgress < 100 && (
                  <Progress
                    size="sm"
                    color="info"
                    value={cardUploadProgress}
                    className="max-w-xs"
                  />
                )}
                <div className="space-y-4">
                  {cardAttachments.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">
                        No card
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {cardAttachments.map((att) => (
                          <AttachmentPill
                            key={`card-${att.id}`}
                            att={att}
                            cardId={id}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {taskAttachments.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">
                        Nas tarefas
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {taskAttachments.map((att) => (
                          <AttachmentPill
                            key={`task-${att.id}-${att.taskId}`}
                            att={att}
                            cardId={id}
                            taskId={att.taskId}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {cardAttachments.length === 0 &&
                    taskAttachments.length === 0 && (
                      <span className="text-sm text-slate-500">
                        Nenhum anexo.
                      </span>
                    )}
                </div>
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

/** =============================================================
 *  Subcomponents
 *  ============================================================= */
function InfoCell({ label, value, icon }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3 text-sm dark:border-white/10">
      <div className="mb-1 flex items-center gap-1 text-xs text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="font-medium text-slate-900 dark:text-slate-100">
        {value}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="flex items-center justify-center py-8 text-center">
      <div>
        <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full border border-dashed border-slate-300 text-slate-400">
          {icon}
        </div>
        <div className="font-medium text-slate-900 dark:text-slate-100">
          {title}
        </div>
        {subtitle && <div className="text-sm text-slate-500">{subtitle}</div>}
      </div>
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm dark:border-white/10 dark:bg-[#0b0f1a]">
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-50">
        {value}
      </div>
    </div>
  );
}

function AttachmentPill({ att, cardId, taskId }) {
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    try {
      setLoading(true);
      const ttl = 5 * 60 * 1000;

      const alreadyPublic =
        att.url || att.publicUrl || att.signedUrl || att.downloadUrl;
      if (alreadyPublic) {
        window.open(alreadyPublic, "_blank", "noopener,noreferrer");
        return;
      }

      const endpoint = taskId
        ? CRM.TASK_ATTACHMENT_SIGNED_URL(cardId, taskId, att.id)
        : CRM.CARD_ATTACHMENT_SIGNED_URL(cardId, att.id);

      const { data } = await axiosInstance.get(endpoint, {
        params: { ttl },
      });
      const url = data?.data?.url || data?.url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Erro ao abrir anexo", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button size="xs" color="light" onClick={handleOpen} disabled={loading}>
      <HiPaperClip className="mr-2 h-4 w-4" />
      {att.originalName || att.filename || att.name || `anexo-${att.id}`}
    </Button>
  );
}
